import React, { useState } from "react";
import {
  Card,
  Timeline,
  Button,
  Badge,
  Spin,
  Empty,
  Tag,
  Space,
  Modal,
  message,
  Checkbox,
  Popconfirm,
} from "antd";
import {
  UserOutlined,
  ClockCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import {
  useSchedulesForDateRange,
  useDeleteSchedule,
  Schedule,
  useStaff,
} from "../../hooks/useSchedule";
import ScheduleFilter, { FilterValues } from "./ScheduleFilter";
import ScheduleFormModal from "./ScheduleFormModal";
import ScheduleViewModal from "./ScheduleViewModal";
import {
  filterSchedules,
  getBadgeStatus,
  capitalize,
} from "../../helpers/schedule";
import { useLocations } from "../../hooks/useUsers";
import { PERMISSIONS, usePermissions } from "../../context/PermissionContext";

interface ScheduleTimelineProps {
  locationId?: number;
  canCreateSchedules: boolean;
  canModifySchedules: boolean;
  canDeleteSchedules: boolean;
}

// Helper function to get current user from localStorage
const getCurrentUser = () => {
  try {
    const userData =
      localStorage.getItem("user") || localStorage.getItem("currentUser");
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error("Error getting current user from localStorage:", error);
    return null;
  }
};

const ScheduleTimeline: React.FC<ScheduleTimelineProps> = ({
  locationId,
  canCreateSchedules,
  canModifySchedules,
  canDeleteSchedules,
}) => {
  // Permission checks
  const { hasPermission } = usePermissions();
  const canSeeAllLocations = hasPermission(PERMISSIONS.CAN_SEE_ALL_LOCATIONS);
  const canSeeAllSchedules = hasPermission(PERMISSIONS.CAN_SEE_ALL_SCHEDULES);

  const currentUser = getCurrentUser();

  // State - Start from today, show next 7 days by default
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs(),
    dayjs().add(7, "day"),
  ]);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(
    null
  );
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);

  // Bulk delete state
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<number[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Filters state - Start from today
  const [filters, setFilters] = useState<FilterValues>({
    location_id: canSeeAllLocations ? locationId : currentUser?.location_id,
    date_range: dateRange,
  });

  // Calculate date range from filters
  const startDate =
    filters.date_range?.[0]?.format("YYYY-MM-DD") ||
    dayjs().format("YYYY-MM-DD");
  const endDate =
    filters.date_range?.[1]?.format("YYYY-MM-DD") ||
    dayjs().add(7, "day").format("YYYY-MM-DD");

  // API Hooks - Convert UI filters to API filters
  const apiFilters = React.useMemo(() => {
    const processedFilters = { ...filters };

    // Remove user_ids from API call since API doesn't support it
    if ("user_ids" in processedFilters) {
      delete processedFilters.user_ids;
    }

    // Remove date_range from API call since API expects separate start_date/end_date parameters
    if ("date_range" in processedFilters) {
      delete processedFilters.date_range;
    }

    // Handle user filtering logic
    if (!canSeeAllSchedules && currentUser?.id) {
      // If user can't see all schedules, always use their own ID regardless of selection
      processedFilters.user_id = currentUser.id;
    } else if (filters.user_ids && filters.user_ids.length > 0) {
      // If user can see all schedules and has selected someone, use the first selected user
      processedFilters.user_id = filters.user_ids[0];
    }

    return processedFilters;
  }, [filters, canSeeAllSchedules, currentUser?.id]);

  const { data: schedulesData, isLoading: isLoadingSchedules } =
    useSchedulesForDateRange(startDate, endDate, apiFilters);
  const deleteScheduleMutation = useDeleteSchedule();

  // Get locations and staff
  const { data: locationsData, isLoading: isLoadingLocations } = useLocations();
  const { data: staffData, isLoading: isLoadingStaff } = useStaff(
    canSeeAllLocations ? undefined : currentUser?.location_id
  );

  // Filter schedules and sort by date and time
  const filteredSchedules = React.useMemo(() => {
    const schedules = schedulesData?.data || [];
    const filtered = filterSchedules(schedules, filters);

    // Sort by date and time (earliest first)
    return filtered.sort((a: Schedule, b: Schedule) => {
      const dateA = dayjs(a.schedule_date + " " + a.start_time);
      const dateB = dayjs(b.schedule_date + " " + b.start_time);
      return dateA.valueOf() - dateB.valueOf();
    });
  }, [schedulesData, filters]);

  // Status colors
  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "green";
      case "scheduled":
        return "blue";
      case "canceled":
        return "red";
      default:
        return "gray";
    }
  };

  // Render shift type tag
  const renderShiftTag = (shift: string) => {
    const color =
      shift === "morning" ? "blue" : shift === "day" ? "orange" : "purple";
    return <Tag color={color}>{shift.toUpperCase()}</Tag>;
  };

  // Permission-aware handlers
  const handleFilter = (values: FilterValues) => {
    if (values.date_range && values.date_range.length === 2) {
      setDateRange([values.date_range[0], values.date_range[1]]);
    }
    setFilters(values);
    setIsFilterVisible(false);
  };

  const handleResetFilter = () => {
    const newDateRange: [Dayjs, Dayjs] = [dayjs(), dayjs().add(7, "day")];
    setDateRange(newDateRange);

    setFilters({
      location_id: canSeeAllLocations ? locationId : currentUser?.location_id,
      date_range: newDateRange,
    });
    setIsFilterVisible(false);
  };

  const handleCreateSchedule = () => {
    if (!canCreateSchedules) {
      message.error("You don't have permission to create schedules");
      return;
    }
    setSelectedSchedule(null);
    setIsFormModalVisible(true);
  };

  const handleEditSchedule = (schedule: Schedule) => {
    if (!canModifySchedules) {
      message.error("You don't have permission to edit schedules");
      return;
    }
    setSelectedSchedule(schedule);
    setIsViewModalVisible(false);
    setIsFormModalVisible(true);
  };

  const handleViewSchedule = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setIsViewModalVisible(true);
  };

  const handleDeleteSchedule = (id: number) => {
    if (!canDeleteSchedules) {
      message.error("You don't have permission to delete schedules");
      return;
    }
    Modal.confirm({
      title: "Delete Schedule",
      icon: <ExclamationCircleOutlined />,
      content:
        "Are you sure you want to delete this schedule? This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteScheduleMutation.mutateAsync(id);
          setIsViewModalVisible(false);
        } catch (error) {
          console.error("Error deleting schedule:", error);
        }
      },
    });
  };

  // Bulk delete handlers for timeline
  const handleBulkDeleteTimeline = async () => {
    if (!canDeleteSchedules) {
      message.error("You don't have permission to delete schedules");
      return;
    }

    if (selectedScheduleIds.length === 0) {
      message.warning("Please select schedules to delete");
      return;
    }

    // Limit bulk delete to 10 items
    if (selectedScheduleIds.length > 10) {
      message.warning(
        "You can only delete up to 10 schedules at once. Please select fewer items."
      );
      return;
    }

    Modal.confirm({
      title: `Delete ${selectedScheduleIds.length} Schedule${
        selectedScheduleIds.length > 1 ? "s" : ""
      }`,
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete ${
        selectedScheduleIds.length
      } selected schedule${
        selectedScheduleIds.length > 1 ? "s" : ""
      }? This action cannot be undone.`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setIsBulkDeleting(true);
        let successCount = 0;
        let errorCount = 0;

        try {
          // Delete each schedule individually since there's no bulk API
          for (const scheduleId of selectedScheduleIds) {
            try {
              await deleteScheduleMutation.mutateAsync(scheduleId);
              successCount++;
            } catch (error) {
              console.error(`Failed to delete schedule ${scheduleId}:`, error);
              errorCount++;
            }
          }

          // Show results
          if (successCount > 0) {
            message.success(
              `Successfully deleted ${successCount} schedule${
                successCount > 1 ? "s" : ""
              }`
            );
          }
          if (errorCount > 0) {
            message.error(
              `Failed to delete ${errorCount} schedule${
                errorCount > 1 ? "s" : ""
              }`
            );
          }

          // Clear selection
          setSelectedScheduleIds([]);
        } finally {
          setIsBulkDeleting(false);
        }
      },
    });
  };

  const handleScheduleSelection = (scheduleId: number, checked: boolean) => {
    if (checked) {
      // Check if adding this would exceed the limit
      if (selectedScheduleIds.length >= 10) {
        message.warning(
          "You can only select up to 10 schedules for bulk operations"
        );
        return;
      }
      setSelectedScheduleIds((prev) => [...prev, scheduleId]);
    } else {
      setSelectedScheduleIds((prev) => prev.filter((id) => id !== scheduleId));
    }
  };

  const handleSelectAllTimeline = (checked: boolean) => {
    if (checked) {
      // Select up to 10 schedules
      const allIds = filteredSchedules
        .slice(0, 10)
        .map((schedule) => schedule.id);
      setSelectedScheduleIds(allIds);
      if (filteredSchedules.length > 10) {
        message.info("Only the first 10 schedules were selected (bulk limit)");
      }
    } else {
      setSelectedScheduleIds([]);
    }
  };

  // Render timeline item with permission-aware actions and checkbox
  const renderTimelineItem = (schedule: Schedule) => {
    const isSelected = selectedScheduleIds.includes(schedule.id);
    const scheduleDateTime = dayjs(
      schedule.schedule_date + " " + schedule.start_time
    );
    const isToday = dayjs(schedule.schedule_date).isSame(dayjs(), "day");
    const isPast = scheduleDateTime.isBefore(dayjs());

    return (
      <Timeline.Item
        key={schedule.id}
        color={getStatusColor(schedule.status)}
        label={
          <div className="text-right">
            <div className={`${isToday ? "font-bold text-blue-600" : ""}`}>
              {dayjs(schedule.schedule_date).format("MMM DD, YYYY")}
              {isToday && <span className="text-xs block">Today</span>}
            </div>
            <div className={`text-gray-500 ${isPast ? "text-red-400" : ""}`}>
              {dayjs(schedule.start_time, "HH:mm:ss").format("h:mm A")}
            </div>
          </div>
        }
      >
        <div
          className={`bg-gray-50 p-3 rounded-lg border-l-4 cursor-pointer hover:bg-gray-100 transition-colors ${
            isToday ? "bg-blue-50 border-blue-200" : ""
          } ${isPast ? "opacity-75" : ""}`}
          style={{ borderLeftColor: getStatusColor(schedule.status) }}
        >
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center space-x-2">
              {canDeleteSchedules && (
                <Checkbox
                  checked={isSelected}
                  onChange={(e) =>
                    handleScheduleSelection(schedule.id, e.target.checked)
                  }
                  onClick={(e) => e.stopPropagation()}
                  disabled={!isSelected && selectedScheduleIds.length >= 10}
                />
              )}
              <div onClick={() => handleViewSchedule(schedule)}>
                <div className="font-medium">{schedule.user.name}</div>
                <div className="text-gray-500 flex items-center mt-1">
                  Table: {schedule.table.number}
                </div>
                {canSeeAllLocations && (
                  <div className="text-gray-500 flex items-center mt-1">
                    <UserOutlined className="mr-1" />
                    {schedule.user.location.name}
                  </div>
                )}
              </div>
            </div>
            <Space>
              {renderShiftTag(schedule.shift_type)}
              <Badge
                status={getBadgeStatus(schedule.status)}
                text={capitalize(schedule.status)}
              />
            </Space>
          </div>

          <div className="mt-2 flex items-center text-gray-600">
            <ClockCircleOutlined className="mr-1" />
            {dayjs(schedule.start_time, "HH:mm:ss").format("h:mm A")} -{" "}
            {dayjs(schedule.end_time, "HH:mm:ss").format("h:mm A")}
            {isPast && (
              <span className="ml-2 text-red-400 text-xs">(Past)</span>
            )}
          </div>

          {schedule.notes && (
            <div className="mt-2 text-gray-600 italic">{schedule.notes}</div>
          )}

          <div className="mt-3 flex justify-end">
            <Space>
              {canModifySchedules && (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditSchedule(schedule);
                  }}
                >
                  Edit
                </Button>
              )}
              {canDeleteSchedules && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSchedule(schedule.id);
                  }}
                >
                  Delete
                </Button>
              )}
            </Space>
          </div>
        </div>
      </Timeline.Item>
    );
  };

  // Show total schedules count for debugging
  React.useEffect(() => {
    if (schedulesData) {
      console.log(
        `Timeline: Loaded ${schedulesData.total} schedules for ${startDate} to ${endDate}, filtered to ${filteredSchedules.length}`,
        { canSeeAllSchedules, currentUser: currentUser?.id, apiFilters }
      );
    }
  }, [
    schedulesData,
    filteredSchedules,
    startDate,
    endDate,
    canSeeAllSchedules,
    currentUser,
    apiFilters,
  ]);

  // Prepare locations and staff data for filters
  const locations = canSeeAllLocations ? locationsData || [] : [];
  const staff = staffData?.data || [];

  if (isLoadingSchedules || isLoadingLocations || isLoadingStaff) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <Spin size="large" />
        <div className="mt-4 text-gray-600">
          {isLoadingSchedules &&
            `Loading schedules for ${dayjs(startDate).format(
              "MMM DD"
            )} - ${dayjs(endDate).format("MMM DD, YYYY")}...`}
          {isLoadingLocations && "Loading locations..."}
          {isLoadingStaff && "Loading staff..."}
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-timeline">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">
          {canSeeAllSchedules
            ? "Staff Schedule Timeline"
            : "My Schedule Timeline"}
        </h2>
        <div className="flex space-x-2">
          {selectedScheduleIds.length > 0 && canDeleteSchedules && (
            <Popconfirm
              title={`Delete ${selectedScheduleIds.length} selected schedule${
                selectedScheduleIds.length > 1 ? "s" : ""
              }?`}
              description="This action cannot be undone."
              onConfirm={handleBulkDeleteTimeline}
              okText="Yes, Delete"
              cancelText="Cancel"
              okType="danger"
            >
              <Button danger loading={isBulkDeleting} icon={<DeleteOutlined />}>
                Delete {selectedScheduleIds.length} Selected (max 10)
              </Button>
            </Popconfirm>
          )}

          <Button onClick={() => setIsFilterVisible(!isFilterVisible)}>
            {isFilterVisible ? "Hide Filters" : "Show Filters"}
          </Button>
          {canCreateSchedules && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateSchedule}
            >
              Add Schedule
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      {isFilterVisible && (
        <ScheduleFilter
          initialValues={filters}
          onFilter={handleFilter}
          onReset={handleResetFilter}
          locations={locations}
          staff={staff}
          canSeeAllSchedules={canSeeAllSchedules}
        />
      )}

      {/* Timeline */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            {canSeeAllSchedules
              ? "Staff Schedule Timeline"
              : "My Schedule Timeline"}
            <span className="text-gray-500 ml-2 font-normal">
              {filters.date_range && filters.date_range.length === 2
                ? `${filters.date_range[0].format(
                    "MMM DD"
                  )} - ${filters.date_range[1].format("MMM DD, YYYY")}`
                : ""}
            </span>
          </h3>

          {canDeleteSchedules && filteredSchedules.length > 0 && (
            <Checkbox
              checked={
                selectedScheduleIds.length ===
                Math.min(filteredSchedules.length, 10)
              }
              indeterminate={
                selectedScheduleIds.length > 0 &&
                selectedScheduleIds.length <
                  Math.min(filteredSchedules.length, 10)
              }
              onChange={(e) => handleSelectAllTimeline(e.target.checked)}
            >
              Select All ({Math.min(filteredSchedules.length, 10)} max)
            </Checkbox>
          )}
        </div>

        {filteredSchedules.length === 0 ? (
          <Empty description="No schedules found for the selected filters" />
        ) : (
          <div className="max-h-[600px] overflow-y-auto">
            <Timeline mode="left">
              {filteredSchedules.map((schedule: Schedule) =>
                renderTimelineItem(schedule)
              )}
            </Timeline>
          </div>
        )}
      </Card>

      {/* Create/Edit Schedule Form Modal */}
      <ScheduleFormModal
        visible={isFormModalVisible}
        onCancel={() => setIsFormModalVisible(false)}
        onSubmit={() => setIsFormModalVisible(false)}
        schedule={selectedSchedule ?? undefined}
        locationFilter={filters.location_id}
      />

      {/* View Schedule Modal */}
      <ScheduleViewModal
        visible={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        onEdit={handleEditSchedule}
        onDelete={handleDeleteSchedule}
        schedule={selectedSchedule}
        isDeleting={deleteScheduleMutation.isPending}
        canModifySchedules={canModifySchedules}
        canDeleteSchedules={canDeleteSchedules}
      />
    </div>
  );
};

export default ScheduleTimeline;
